import { formatDate, NgIf, AsyncPipe, CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Component, OnInit, ChangeDetectionStrategy, Inject, LOCALE_ID, ViewChild, ElementRef } from '@angular/core';
import { AsamcoAuthService, AsamcoUserService, Permissions } from '@asamco/base';
import { User, UserService } from '@asamco/user-management/shared';
import { pathJoin, skipAuthInterceptor } from '@asamco/utils';
import { BehaviorSubject, combineLatest, from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuditLogData } from '../../interfaces/audit-log-data.interface';
import { Company } from '../../interfaces/company.interface';
import { LinkTemplate } from '../../interfaces/link-template.interface';
import { LinkType } from '../../interfaces/link-type.interface';
import { SelectedCompanyService } from '../../selected-company/selected-company.service';
import { LinkTemplateService } from '../../services/link-template.service';
import { LinkTypeService } from '../../services/link-type.service';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxoSummaryModule, DxiGroupItemModule, DxiSortByGroupSummaryInfoModule, DxiColumnModule, DxoSelectionModule, DxoSearchPanelModule, DxoPagingModule, DxoGroupPanelModule, DxoGroupingModule } from 'devextreme-angular/ui/nested';
import { DxButtonModule, DxDataGridModule, DxTextBoxModule } from 'devextreme-angular';
import { ApplicationInsightsService } from '../../services/application-insights.service';
import DevExpress from 'devextreme';
import { DxDataGridComponent, DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import ODataStore from 'devextreme/data/odata/store';

@Component({
    selector: 'app-shell-admin-financial-issues-overview',
    templateUrl: './admin-financial-issues-overview.component.html',
    styleUrls: ['./admin-financial-issues-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
		CommonModule,
        DxDataGridModule,
        DxoSummaryModule,
        DxiGroupItemModule,
        DxiSortByGroupSummaryInfoModule,
        DxiColumnModule,
        DxTemplateModule,
        NgIf,
		DxTextBoxModule,
        DxoSelectionModule,
        DxoSearchPanelModule,
        DxoPagingModule,
        DxoGroupPanelModule,
        DxoGroupingModule,
		DxButtonModule,
        AsyncPipe,
    ],
})
	export class AdminFinancialIssuesOverviewComponent implements OnInit {
		@ViewChild(DxDataGridComponent, { static: false }) grid!: DxDataGridComponent;
		@ViewChild('traceparentSearchSection', { static: false }) traceparentSearchSection?: ElementRef<HTMLElement>;

		public jobLogSourceOData: ODataStore<any[]> = null;
		public hasInsightsAccess = false;

	traceparentInfo$: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
	traceparentLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	traceparentStatus$: BehaviorSubject<string> = new BehaviorSubject<string>('Paste the full support error or a traceparent ID and we will extract the trace automatically.');
	traceparentCopied$: BehaviorSubject<string> = new BehaviorSubject<string>('');

	stateSortOrder: string;

	traceparentId: string;
	private copyFeedbackTimer?: ReturnType<typeof setTimeout>;
	private traceparentAutoSearchTimer?: ReturnType<typeof setTimeout>;
	private lastSearchedTraceparentId?: string;
	private readonly traceparentPattern = /\b00-[0-9a-f]{32}-[0-9a-f]{16}-[0-9a-f]{2}\b/i;

		constructor(
			@Inject(LOCALE_ID) private locale: string,
			private asamcoUserService: AsamcoUserService,
			private userService: UserService,
		private companyService: SelectedCompanyService,
		private auth: AsamcoAuthService,
		private linkTemplateService: LinkTemplateService,
		private linkTypeService: LinkTypeService,
		private http: HttpClient,
		private applicationInsightsService: ApplicationInsightsService,
		private asamcoAuthService: AsamcoAuthService
	) {}

		ngOnInit(): void {
			this.stateSortOrder = 'desc';
			void this.initializeAccess();
			this.asamcoAuthService.authTokenChanged$.subscribe(
				(token) => {
					if (token.token && this.hasInsightsAccess) {
						// Setup your data source
						this.jobLogSourceOData = new ODataStore<any[]>({
						deserializeDates: false,
						url: this.asamcoAuthService.userSession.backend.odataBaseUrl + 'JobLogAdminInsights',
						version: 4,
						beforeSend: (e) => {
							e.headers = {
								"Authorization": `Bearer ${token.token}`,
							};
						}
					});
					}
				},
			(error) => {
				console.error('Error fetching the token:', error);
			}
			);
		}

		private async initializeAccess(): Promise<void> {
			const assignedPermissions = await this.asamcoUserService.getPermissions();
			this.hasInsightsAccess = assignedPermissions.includes(Permissions.AL3_ADMIN_LINK_OVERVIEW.toLowerCase());
		}

	lastGroupKey: string | null = null;

	shouldShowGroupHeader(rowIndex: number): boolean {
	const gridInstance = this.grid?.instance;
	if (!gridInstance) return false;

	const row = gridInstance.getVisibleRows()[rowIndex];
	if (!row || !row.data) return false;

	const currentKey = row.data.sessionIdentifier;

	if (currentKey !== this.lastGroupKey) {
		this.lastGroupKey = currentKey;
		return true;
	}

	return false;
	}

	logObject(e: any) {
		console.log(e);
	}

	sendTraceparentid(): void {
		this.clearTraceparentAutoSearchTimer();
		this.searchTraceparent(this.traceparentId);
	}

	searchTraceparent(rawValue: string | null | undefined): void {
		const extractedTraceparentId = this.extractTraceparentId(rawValue);

		this.traceparentCopied$.next('');

		if (!extractedTraceparentId) {
			this.traceparentInfo$.next([]);
			this.traceparentStatus$.next('No traceparent ID found. Paste the full support error or a value like 00-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx-xxxxxxxxxxxxxxxx-00.');
			return;
		}

		this.traceparentId = extractedTraceparentId;
		this.lastSearchedTraceparentId = extractedTraceparentId.toLowerCase();
		this.traceparentLoading$.next(true);
		this.traceparentStatus$.next(`Searching Application Insights for ${extractedTraceparentId}...`);

		this.applicationInsightsService.getTraceparentInfo(extractedTraceparentId).subscribe({
			next: (result) => {
				const normalizedResult = Array.isArray(result)
					? result
					: result
						? [result]
						: [];

				this.traceparentInfo$.next(normalizedResult);
				this.traceparentStatus$.next(
					normalizedResult.length > 0
						? `Showing ${normalizedResult.length} Application Insights result${normalizedResult.length === 1 ? '' : 's'} for ${extractedTraceparentId}. Click any value to copy it.`
						: `No Application Insights results were returned for ${extractedTraceparentId}.`
				);
				this.traceparentLoading$.next(false);
			},
			error: () => {
				this.traceparentInfo$.next([]);
				this.traceparentStatus$.next(`Traceparent lookup failed for ${extractedTraceparentId}.`);
				this.traceparentLoading$.next(false);
			}
		});
	}

	onTraceparentInputChange(value: string | null | undefined): void {
		this.traceparentId = value ?? '';
		this.traceparentCopied$.next('');

		const extractedTraceparentId = this.extractTraceparentId(this.traceparentId);
		if (!extractedTraceparentId) {
			this.clearTraceparentAutoSearchTimer();
			this.traceparentStatus$.next(
				this.traceparentId.trim().length > 0
					? 'Keep typing or paste the full support error. Search starts automatically as soon as a valid traceparent ID is detected.'
					: 'Paste the full support error or a traceparent ID and we will extract the trace automatically.'
			);
			return;
		}

		const normalizedTraceparentId = extractedTraceparentId.toLowerCase();
		if (this.lastSearchedTraceparentId === normalizedTraceparentId && !this.traceparentLoading$.value) {
			this.traceparentStatus$.next(`Trace detected: ${extractedTraceparentId}. Results are already shown below.`);
			return;
		}

		this.traceparentStatus$.next(`Trace detected: ${extractedTraceparentId}. Searching automatically...`);
		this.clearTraceparentAutoSearchTimer();
		this.traceparentAutoSearchTimer = setTimeout(() => {
			this.searchTraceparent(this.traceparentId);
		}, 180);
	}

	canSearchTraceparent(value: string | null | undefined): boolean {
		return !!this.extractTraceparentId(value);
	}

	useTraceparentFromMessage(message: string | null | undefined): void {
		this.searchTraceparent(message);
		this.traceparentSearchSection?.nativeElement.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
	}

	getMessageFromCell(cell: any): string {
		const row = cell?.row?.data ?? cell?.data ?? {};
		return this.getPrimaryErrorText(row);
	}

	getPrimaryErrorText(row: any): string {
		const candidates = [
			row?.col_4_val,
			row?.col_1_val,
			row?.col_2_val,
			row?.col_3_val,
			row?.col_5_val,
			row?.col_6_val,
		];

		const firstText = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
		return firstText ?? '';
	}

	getAdditionalDetailValuesFromCell(cell: any): string[] {
		const row = cell?.row?.data ?? cell?.data ?? {};
		const primaryText = this.getPrimaryErrorText(row);
		const candidates = [row?.col_1_val, row?.col_2_val, row?.col_3_val, row?.col_4_val, row?.col_5_val, row?.col_6_val];

		return candidates.filter((value) => typeof value === 'string' && value.trim().length > 0 && value !== primaryText);
	}

	useTraceparentFromCell(cell: any): void {
		this.useTraceparentFromMessage(this.getMessageFromCell(cell));
	}

	hasTraceparentInCell(cell: any): boolean {
		return this.hasTraceparent(this.getMessageFromCell(cell));
	}

	getTraceparentIdFromCell(cell: any): string | null {
		return this.extractTraceparentId(this.getMessageFromCell(cell));
	}

	getMessagePrefixFromCell(cell: any): string {
		return this.getMessagePrefix(this.getMessageFromCell(cell));
	}

	getMessageSuffixFromCell(cell: any): string {
		return this.getMessageSuffix(this.getMessageFromCell(cell));
	}

	onTraceparentCodeClick(cell: any, event?: Event): void {
		event?.stopPropagation();
		event?.preventDefault();
		this.useTraceparentFromCell(cell);
	}

	getMessagePrefix(value: string | null | undefined): string {
		const text = value ?? '';
		const traceparentId = this.extractTraceparentId(text);
		if (!traceparentId) {
			return text;
		}

		const startIndex = text.indexOf(traceparentId);
		return startIndex >= 0 ? text.slice(0, startIndex) : text;
	}

	getMessageSuffix(value: string | null | undefined): string {
		const text = value ?? '';
		const traceparentId = this.extractTraceparentId(text);
		if (!traceparentId) {
			return '';
		}

		const startIndex = text.indexOf(traceparentId);
		return startIndex >= 0 ? text.slice(startIndex + traceparentId.length) : '';
	}

	hasTraceparent(message: string | null | undefined): boolean {
		return !!this.extractTraceparentId(message);
	}

	extractTraceparentId(value: string | null | undefined): string | null {
		if (!value) {
			return null;
		}

		const trimmedValue = value.trim();
		const match = trimmedValue.match(this.traceparentPattern);

		return match?.[0] ?? null;
	}

	clearTraceparentSearch(): void {
		this.clearTraceparentAutoSearchTimer();
		this.traceparentId = '';
		this.lastSearchedTraceparentId = undefined;
		this.traceparentInfo$.next([]);
		this.traceparentCopied$.next('');
		this.traceparentStatus$.next('Paste the full support error or a traceparent ID and we will extract the trace automatically.');
	}

	async onCellClick(e: DxDataGridTypes.CellClickEvent): Promise<void> {
		if (e?.rowType !== 'data' || e?.value === null || e?.value === undefined || e?.value === '') {
			return;
		}

		await this.copyToClipboard(String(e.value));
	}

	private async copyToClipboard(value: string): Promise<void> {
		try {
			if (navigator?.clipboard?.writeText) {
				await navigator.clipboard.writeText(value);
			} else {
				const textArea = document.createElement('textarea');
				textArea.value = value;
				textArea.style.position = 'fixed';
				textArea.style.left = '-9999px';
				document.body.appendChild(textArea);
				textArea.select();
				document.execCommand('copy');
				document.body.removeChild(textArea);
			}

			this.setCopyFeedback('Copied to clipboard.');
		} catch (err) {
			console.error('Failed to copy: ', err);
			this.setCopyFeedback('Copy failed.');
		}
	}

	private setCopyFeedback(message: string): void {
		if (this.copyFeedbackTimer) {
			clearTimeout(this.copyFeedbackTimer);
		}

		this.traceparentCopied$.next(message);
		this.copyFeedbackTimer = setTimeout(() => this.traceparentCopied$.next(''), 1600);
	}

	private clearTraceparentAutoSearchTimer(): void {
		if (this.traceparentAutoSearchTimer) {
			clearTimeout(this.traceparentAutoSearchTimer);
			this.traceparentAutoSearchTimer = undefined;
		}
	}

	padGroupingText(obj: any): string {
		const dataObj = obj.data;
		if (!dataObj) return '';

		const date = `[${formatDate(dataObj.creationDate, 'dd/MM/yyyy HH:mm', this.locale)}]`;
		const companyIdentifier = `[${dataObj.fullCompanyIdentifier}]`;
		const from = dataObj.fromLinkTemplateDescription;
		const to = dataObj.toLinkTemplateDescription;
		const link = `[${dataObj.linkName}]`;
		const issueCount = dataObj.issueCount || 1;
		const issueString = issueCount === 1 ? 'issue' : 'issues';

		return `${date} ${companyIdentifier} - ${link} - ${from} - ${to} - [${issueCount} ${issueString}]`;
	  }

	// padGroupingText(obj: any) {
	// 	let dataObj = null;
	// 	let issueCount: number = 0;
	// 	let issueString = 'issue';
	// 	if (obj.data?.collapsedItems) {
	// 		dataObj = obj.data.collapsedItems[0];
	// 		issueCount = obj.data.collapsedItems.length;
	// 	} else if (obj.data?.items) {
	// 		dataObj = obj.data.items[0];
	// 		issueCount = obj.data.items.length;
	// 	}

	// 	let date: string = '[' + formatDate(dataObj.creationDate, 'dd/MM/yyyy HH:mm', this.locale) + ']';
	// 	let companyIdentifier: string = '[' + dataObj.fullCompanyIdentifier + ']';
	// 	let fromLinkTemplateDescription: string = dataObj.fromLinkTemplateDescription;
	// 	let toLinkTemplateDescription: string = dataObj.toLinkTemplateDescription;
	// 	let linkName: string = '[' + dataObj.linkName + ']';

	// 	if (issueCount > 1) {
	// 		issueString = 'issues';
	// 	}
	// 	let fullString: string =
	// 		date +
	// 		' ' +
	// 		companyIdentifier +
	// 		' - ' +
	// 		linkName +
	// 		' - ' +
	// 		fromLinkTemplateDescription +
	// 		' - ' +
	// 		toLinkTemplateDescription +
	// 		' - ' +
	// 		'[' +
	// 		issueCount +
	// 		' ' +
	// 		issueString +
	// 		']';
	// 	return fullString;
	// 	//| date:'d/MM/yyyy H:mm' }}] - [{{obj.data.collapsedItems[0].fullCompanyIdentifier}}] {{obj.data.collapsedItems[0].fromLinkTemplateDescription}} - {{obj.data.collapsedItems[0].toLinkTemplateDescription}}
	// 	return 'ok';
	// }

	calculateCellValue(rowData: any) {
		return rowData.id;
	}
}
